import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="search"
export default class extends Controller {
  static targets = ["input", "clearButton", "container"];

  // Clear the search input
  clear() {
    // Don't allow clearing while loading
    if (this.isLoading) return;

    this.inputTarget.value = "";
    this.inputTarget.focus();
    this.toggleClearButton();
    this.performSearch();
  }

  connect() {
    this.debounceTimeout = null;
    this.debounceDelay = 500; // 500ms debounce delay
    this.isLoading = false;
    this.toggleClearButton();

    // Restore focus if there's content (after page reload or Turbo navigation)
    this.restoreFocus();

    // Listen for Turbo events to restore focus after navigation
    document.addEventListener("turbo:load", this.handleTurboLoad.bind(this));
    document.addEventListener("turbo:frame-load", this.handleTurboLoad.bind(this));
    document.addEventListener("turbo:submit-start", this.handleSubmitStart.bind(this));
    document.addEventListener("turbo:submit-end", this.handleSubmitEnd.bind(this));
  }

  disconnect() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Clean up event listeners
    document.removeEventListener("turbo:load", this.handleTurboLoad.bind(this));
    document.removeEventListener("turbo:frame-load", this.handleTurboLoad.bind(this));
    document.removeEventListener("turbo:submit-start", this.handleSubmitStart.bind(this));
    document.removeEventListener("turbo:submit-end", this.handleSubmitEnd.bind(this));
  }

  // Get X icon HTML that matches rails_icons output
  getXIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" class="size-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m18 6-12 12"/><path d="m6 6 12 12"/></svg>';
  }

  handleSubmitEnd(event) {
    // Check if this is our form being submitted
    const form = this.inputTarget.closest("form");
    if (form && event.target === form) {
      this.isLoading = false;
      this.updateClearButton();
    }
  }

  handleSubmitStart(event) {
    // Check if this is our form being submitted
    const form = this.inputTarget.closest("form");
    if (form && event.target === form) {
      this.isLoading = true;
      this.updateClearButton();
    }
  }

  handleTurboLoad() {
    // Small delay to ensure the DOM is fully updated
    setTimeout(() => {
      this.restoreFocus();
    }, 50);
  }

  // Called when user types in the search input
  input() {
    // Clear any existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Set up debounced search
    this.debounceTimeout = setTimeout(() => {
      this.performSearch();
    }, this.debounceDelay);

    // Always update clear button visibility immediately
    this.toggleClearButton();
  }

  // Perform the actual search (submit the form)
  performSearch() {
    // Find the form that contains this search input
    const form = this.inputTarget.closest("form");
    if (form) {
      form.requestSubmit();
    }
  }

  restoreFocus() {
    if (this.inputTarget.value.trim().length > 0) {
      this.inputTarget.focus();
      // Move cursor to end of input
      this.inputTarget.setSelectionRange(this.inputTarget.value.length, this.inputTarget.value.length);
    }
  }

  // Toggle visibility of clear button based on input content
  toggleClearButton() {
    this.updateClearButton();
  }

  // Update clear button visibility and content based on loading state
  updateClearButton() {
    if (this.hasClearButtonTarget) {
      const hasContent = this.inputTarget.value.trim().length > 0;

      if (hasContent) {
        this.clearButtonTarget.style.display = "flex";

        if (this.isLoading) {
          // Show spinner while loading
          this.clearButtonTarget.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" class="size-4 animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
          this.clearButtonTarget.disabled = true;
        } else {
          // Show X icon when not loading
          this.clearButtonTarget.innerHTML = this.getXIcon();
          this.clearButtonTarget.disabled = false;
        }
      } else {
        this.clearButtonTarget.style.display = "none";
      }
    }
  }
}
